import React, { useState } from 'react';
import { Instagram, Youtube } from 'lucide-react';
type LegalModal = 'terms' | 'privacy' | null;
function TikTokIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true" data-id="element-38">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.51a8.16 8.16 0 0 0 4.77 1.52V6.59a4.85 4.85 0 0 1-1.84-.1z" data-id="element-39" />
    </svg>;
}
const socials = [{
  name: 'Instagram',
  href: 'https://www.instagram.com/soycarelizgriman?igsh=MWFpcDMyZjg1ejJscg%3D%3D&utm_source=qr',
  Icon: Instagram
}, {
  name: 'YouTube',
  href: 'https://youtube.com/@carelizgriman?si=sfBZOq9wWIhPjj0e',
  Icon: Youtube
}, {
  name: 'TikTok',
  href: 'https://www.tiktok.com/@soycarelizgriman?_r=1&_t=ZS-96TRRhTFqXE',
  Icon: TikTokIcon
}];
export function Footer() {
  const [openModal, setOpenModal] = useState<LegalModal>(null);
  return <>
      <footer className="py-12 bg-brand-brown border-t border-brand-cream/10 text-center px-6" data-id="element-40">
        <div className="max-w-3xl mx-auto space-y-6" data-id="element-41">
          {/* Social icons — shiny gold */}
          <div className="flex items-center justify-center gap-5" data-id="element-42">
            {socials.map(({
            name,
            href,
            Icon
          }) => <a key={name} href={href} target="_blank" rel="noopener noreferrer" aria-label={`Visita ${name} de Careliz Griman`} className="group relative w-12 h-12 rounded-full flex items-center justify-center border-2 border-brand-gold bg-brand-brown-light/40 hover:bg-brand-brown-light transition-all duration-300 hover:-translate-y-0.5 shadow-[0_0_15px_rgba(201,168,118,0.25)] hover:shadow-[0_0_25px_rgba(201,168,118,0.5)]" data-id="element-43">
                <Icon className="w-5 h-5 transition-colors duration-300" style={{
              color: '#E6CFA0',
              filter: 'drop-shadow(0 1px 2px rgba(201,168,118,0.4))'
            }} data-id="element-44" />
              </a>)}
          </div>

          <p className="text-brand-cream/60 font-light text-sm" data-id="element-45">
            © {new Date().getFullYear()} Careliz Griman. Todos los derechos
            reservados.
          </p>
          <div className="flex items-center justify-center gap-6 text-sm" data-id="element-46">
            <button onClick={() => setOpenModal('terms')} className="text-brand-cream/70 hover:text-brand-gold transition-colors duration-300 font-light" data-id="element-47">
              Términos y Condiciones
            </button>
            <span className="text-brand-cream/30" data-id="element-48">•</span>
            <button onClick={() => setOpenModal('privacy')} className="text-brand-cream/70 hover:text-brand-gold transition-colors duration-300 font-light" data-id="element-49">
              Política de Privacidad
            </button>
          </div>
        </div>
      </footer>

      {openModal && <LegalModal type={openModal} onClose={() => setOpenModal(null)} data-id="element-50" />}
    </>;
}
function LegalModal({
  type,
  onClose
}: {
  type: 'terms' | 'privacy';
  onClose: () => void;
}) {
  const content = type === 'terms' ? termsContent : privacyContent;
  return <div className="fixed inset-0 z-50 bg-brand-brown/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} role="dialog" aria-modal="true" data-id="element-51">
      <div className="bg-brand-cream max-w-2xl w-full max-h-[85vh] overflow-y-auto rounded-2xl shadow-2xl relative" onClick={e => e.stopPropagation()} data-id="element-52">
        <div className="sticky top-0 bg-brand-cream border-b border-brand-gold/20 px-8 py-5 flex items-center justify-between" data-id="element-53">
          <h3 className="text-2xl font-serif text-brand-brown" data-id="element-54">
            {content.title}
          </h3>
          <button onClick={onClose} className="text-brand-brown/60 hover:text-brand-brown transition-colors w-8 h-8 flex items-center justify-center text-xl" aria-label="Cerrar" data-id="element-55">
            ×
          </button>
        </div>
        <div className="px-8 py-6 space-y-5 text-brand-brown/80 font-light leading-relaxed text-sm" data-id="element-56">
          {content.sections.map((section, i) => <div key={i} data-id="element-57">
              <h4 className="font-serif text-brand-brown text-base mb-2" data-id="element-58">
                {section.heading}
              </h4>
              <p data-id="element-59">{section.body}</p>
            </div>)}
          <p className="text-xs text-brand-brown/50 italic pt-4 border-t border-brand-gold/20" data-id="element-60">
            Última actualización:{' '}
            {new Date().toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long'
          })}
          </p>
        </div>
      </div>
    </div>;
}
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: '1. Sobre la mentoría',
    body: 'Atajo al Éxito es una mentoría 1:1 personalizada impartida por Careliz Griman. Incluye una sesión privada por Zoom de 90 minutos, plan de acción personalizado, grabación de la sesión y acompañamiento vía WhatsApp por 30 días.'
  }, {
    heading: '2. Pago y reserva',
    body: 'La inversión es de USD 147 (o su equivalente en pesos argentinos). El pago debe realizarse de forma anticipada para confirmar la reserva del espacio. Una vez confirmado el pago, recibirás un enlace para agendar tu sesión.'
  }, {
    heading: '3. Política de cancelaciones',
    body: 'Puedes reprogramar tu sesión con un mínimo de 48 horas de anticipación. No se realizan reembolsos una vez confirmada la reserva, dado el carácter personalizado de la mentoría.'
  }, {
    heading: '4. Confidencialidad',
    body: 'Todo el contenido compartido durante la sesión es confidencial. La grabación es para uso exclusivo de la mentee y no puede ser compartida, distribuida ni publicada.'
  }, {
    heading: '5. Resultados',
    body: 'La mentoría ofrece estrategia, claridad y acompañamiento. Los resultados dependen del compromiso, acción y contexto individual de cada participante. No se garantizan resultados económicos específicos.'
  }, {
    heading: '6. Contacto',
    body: 'Para cualquier consulta sobre estos términos, puedes escribir directamente a Careliz Griman a través de los canales oficiales de contacto.'
  }]
};
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: '1. Información que recopilamos',
    body: 'Recopilamos información personal que tú nos proporcionas voluntariamente al postularte a la mentoría, incluyendo nombre, correo electrónico, número de teléfono e información sobre tu negocio digital.'
  }, {
    heading: '2. Uso de la información',
    body: 'Tu información se utiliza únicamente para coordinar la mentoría, brindarte el acompañamiento prometido, procesar pagos y mantener comunicación contigo sobre el servicio.'
  }, {
    heading: '3. Protección de datos',
    body: 'Implementamos medidas de seguridad razonables para proteger tu información personal. Tus datos no son compartidos, vendidos ni alquilados a terceros bajo ninguna circunstancia.'
  }, {
    heading: '4. Cookies y análisis',
    body: 'Este sitio puede utilizar cookies y herramientas de análisis para mejorar la experiencia del usuario. Puedes configurar tu navegador para rechazar cookies si lo prefieres.'
  }, {
    heading: '5. Tus derechos',
    body: 'Tienes derecho a acceder, rectificar o solicitar la eliminación de tu información personal en cualquier momento contactándonos directamente.'
  }, {
    heading: '6. Cambios en esta política',
    body: 'Nos reservamos el derecho de actualizar esta política de privacidad. Cualquier cambio será publicado en esta misma página.'
  }]
};